import { Modal, Toast } from '@seiue/ui'
import {
  Plugin,
  SchoolPlugin,
  SchoolPluginScope,
  pluginApi,
} from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'
import { createContext } from 'react'

import { ApplySystemPluginModal } from '../pages/ApplySystemPluginModal'
import { EditUserPluginModal } from '../pages/EditUserPluginModal'

import {
  SystemPluginConfigHandleType,
  SystemPluginConfigType,
  SystemPluginOauthConfigItem,
  TagContextParam,
  UserContextParam,
  UserPluginContextParam,
} from './type'

export enum PluginAppNameEnum {
  Seiue = 'seiue',
  Record = 'record',
  Oa = 'oa',
  Qymail163 = 'qymail163',
  Speed = 'speed',
  Seeyou = 'seeyou',
  Oidc = 'oidc',
  Nocobase = 'nocobase',
}

/**
 * 创建标签 context
 */
export const TagContext = createContext<TagContextParam>({} as AnyType)

/**
 * 创建应用 context
 */
export const UserPluginContext = createContext<UserPluginContextParam>(
  {} as AnyType
)

/**
 * 创建用户首页 context
 */
export const UserContext = createContext<UserContextParam>({} as AnyType)

/**
 * 打开编辑推荐应用全屏弹窗
 *
 * @param schoolPlugin - 当前应用
 */
export const openEditUserPlugin = (schoolPlugin?: SchoolPlugin) => {
  const { hide } = Modal.open({
    header: null,
    footer: null,
    fullScreen: true,
    bodyStyle: { padding: 0 },
    children: (
      <EditUserPluginModal
        onClose={() => {
          hide()
        }}
        schoolPlugin={schoolPlugin}
      />
    ),
  })
}

/**
 * 打开编辑系统应用全屏弹窗
 *
 * @param plugin - 当前应用
 * @param schoolPlugin - 当前用户应用
 */
export const openApplySystemPlugin = (
  plugin: Plugin,
  schoolPlugin?: SchoolPlugin
) => {
  const { hide } = Modal.open({
    header: null,
    footer: null,
    fullScreen: true,
    bodyStyle: { padding: 0 },
    children: (
      <ApplySystemPluginModal
        onClose={() => {
          hide()
        }}
        plugin={plugin}
        schoolPlugin={schoolPlugin}
      />
    ),
  })
}

export const allTagName = '全部应用'
export const noTagName = '未分类应用'

/**
 * 获取应用图标
 *
 * @param plugin - 系统应用
 * @returns 应用图标
 */
export const getSystemPluginIcon = (plugin: Plugin): string => {
  const iconMap: Record<string, string> = {
    seiue: '/icon/seiue.svg',
    record: '/icon/record.svg',
    qymail163: '/icon/mail163.svg',
    oa: '/icon/oa.svg',
    speed: '/icon/speed.svg',
    seeyou: '/icon/seeyou.svg',
    oidc: '/icon/oidc.png',
    nocobase: '/icon/nocobase.png',
  }

  return plugin.icon ?? iconMap[plugin.name || ''] ?? '/defaultapp.svg'
}

/**
 * 获取应用登录配置项
 *
 * @param plugin - 系统应用
 * @returns 应用登录配置项
 */
export const getSystemPluginOauthConfigItems = (plugin: Plugin) => {
  const defaultConfigItems = [
    {
      label: '关联学校 ID',
      name: 'schoolId',
      type: 'number',
    },
    {
      label: '跳转地址',
      name: 'loginUri',
    },
    {
      label: '回调地址',
      name: 'redirectUri',
    },
  ]
  const itemMap: Record<string, SystemPluginOauthConfigItem[]> = {
    seiue: defaultConfigItems,
    record: defaultConfigItems,
    qymail163: [
      {
        label: '企业Open ID',
        name: 'orgOpenId',
      },
      {
        label: '企业邮箱域名',
        name: 'domain',
      },
    ],
    oa: defaultConfigItems,
    speed: defaultConfigItems,
    seeyou: defaultConfigItems,
    oidc: [
      {
        label: '登录回调地址',
        name: 'redirectUri',
      },
      {
        label: '授权类型',
        name: 'grantTypes',
      },
      {
        label: '授权范围',
        name: 'scope',
      },
    ],
    nocobase: [
      {
        label: '登录回调地址',
        name: 'redirectUri',
      },
      {
        label: '授权类型',
        name: 'grantTypes',
      },
      {
        label: '授权范围',
        name: 'scope',
      },
      {
        label: 'Oidc 插件 ID',
        name: 'x_authenticator',
      },
      {
        label: '跳转路径',
        name: 'redirect_uri_path',
      },
    ],
  }

  return itemMap[plugin.name || ''] ?? []
}

/**
 * 数组分组
 *
 * @param arr - 待处理数组
 * @param chunk - 每个数组元素数量
 * @returns 分组数组
 */
export const arrayChunk = (arr: AnyType[], chunk: number): AnyType[] => {
  const result = []
  for (let index = 0; index < arr.length; index += chunk) {
    result.push([arr[index], arr[index + 1]])
  }

  return result
}

const getOpenUrl = async ({
  schoolPlugin,
  reflection,
}: {
  schoolPlugin: SchoolPlugin
  reflection?: { rid: number; usin?: string | null }
}) => {
  const { pluginId, config, entryUrls, plugin } = schoolPlugin
  const { loginUri, schoolId } = config || {}
  const { name: pluginName } = plugin || {}

  if (plugin?.name === PluginAppNameEnum.Qymail163) {
    let qymailUrl = ''
    try {
      const { data: url } = await pluginApi.mailLoginUrl(schoolPlugin.id)

      qymailUrl = url
    } catch (error: AnyType) {
      const { data } = error.response
      Toast.error(data?.message)
    }

    return qymailUrl
  }

  if (plugin?.name === PluginAppNameEnum.Nocobase) {
    const entryUrlStr = `https://${entryUrls?.web || ''}`
    const response = await fetch(entryUrlStr, {
      method: 'POST',
      headers: {
        'x-authenticator': config?.xAuthenticator || '',
      },
      body: JSON.stringify({ redirect: config?.redirectUriPath || '' }),
    })

    const result = await response.json()
    return result.data
  }

  const { rid, usin } = reflection || {}
  switch (true) {
    case pluginId === 0 && !!entryUrls?.web:
      return `http://${entryUrls.web}`
    case rid && !!entryUrls?.web && pluginName === PluginAppNameEnum.Oidc:
      return `http://${entryUrls.web}`
    case !pluginName || !loginUri:
      return ''
    case rid && pluginName === PluginAppNameEnum.Seiue:
      return `${loginUri}?school_id=${schoolId}&carry=${rid}`
    case usin && pluginName === PluginAppNameEnum.Record:
      return `${loginUri}?carry=${usin}`
    case rid &&
      [
        PluginAppNameEnum.Oa,
        PluginAppNameEnum.Speed,
        PluginAppNameEnum.Seeyou,
      ].includes(pluginName as PluginAppNameEnum):
      return `${loginUri}?carry=${rid}`
    default:
      return ''
  }
}

/**
 * 获取点击应用卡片事件
 *
 * @param schoolPlugin - 应用
 * @param isForeground - 是否前台用户
 * @param reflection - 当前用户
 * @param reflection.rid - 当前用户 id
 * @param reflection.usin - 当前用户 usin
 */
export const clickAppCardFunc = async (
  schoolPlugin: SchoolPlugin,
  isForeground?: boolean,
  reflection?: { rid: number; usin?: string | null }
) => {
  if (!isForeground) return

  const openUrl = await getOpenUrl({
    schoolPlugin,
    reflection,
  })

  if (!openUrl) return
  window.open(openUrl, '_blank')
}

/**
 * 对系统应用登录配置表单路径进行格式化处理
 *  - 直接使用数组格式在无数据录入时会导致覆盖
 *
 * @param path - 路径
 * @returns 格式化路径 eg: 1_2
 */
export const handleSystemPluginConfigValue = (path: string[]): string => {
  return path.join('_')
}

/**
 * 处理系统应用登录配置数据
 *
 * @param value - 表单数据
 * @param plugin - 系统应用
 * @returns 格式化数据
 */
export const getSystemPluginConfigValue = (
  value: AnyType,
  plugin: Plugin
): SystemPluginConfigHandleType => {
  const empty = !value.config_clientId && !value.config_clientSecret
  let complated =
    (!!value.config_clientId && !!value.config_clientSecret) || empty
  const config: SystemPluginConfigType = {
    clientId: value.config_clientId,
    clientSecret: value.config_clientSecret,
  }

  const otherItems = getSystemPluginOauthConfigItems(plugin)
  otherItems.map((item) => {
    const dataKey = item.name
    const getKey = handleSystemPluginConfigValue(['config', dataKey])
    config[dataKey] =
      item.type === 'number' ? Number(value[getKey]) : value[getKey]
    if (!empty && !value[getKey]) {
      complated = false
    }
  })

  return { config: empty ? {} : config, complated }
}

/**
 * 初识化显示系统应用登录配置数据
 *
 * @param schoolPlugin - 应用插件
 * @param plugin - 系统插件
 * @returns 初识化数据
 */
export const initSystemPluginConfig = (
  schoolPlugin: SchoolPlugin,
  plugin: Plugin
) => {
  const initConfig: AnyType = schoolPlugin.config || {}
  const config: SystemPluginConfigType = {}
  config[handleSystemPluginConfigValue(['config', 'clientId'])] =
    initConfig.clientId
  config[handleSystemPluginConfigValue(['config', 'clientSecret'])] =
    initConfig.clientSecret

  const otherItems = getSystemPluginOauthConfigItems(plugin)
  otherItems.map((item) => {
    const getKey = item.name
    const dataKey = handleSystemPluginConfigValue(['config', getKey])
    config[dataKey] = initConfig[getKey]
  })

  return config
}

/**
 * 处理最终保存的插件用户范围
 *
 * @param scopes - 用户范围
 * @param showCard - 是否显示卡片
 * @returns 最终保存的用户范围
 */
export const handleSavedPluginScopes = (
  scopes: SchoolPluginScope,
  showCard: boolean
) => (showCard ? scopes : { ids: [], studentIds: [], teacherIds: [] })
